/**
 * AgentOsDemoPricingIntel.jsx — sandbox for Pricing Intelligence Agent.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Sparkles, Loader2, TrendingUp, TrendingDown, Minus, Plus, X, AlertTriangle } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const EXAMPLES = [
  { product_name: "iPhone 15 Pro 256GB", your_price: 1199, your_cost: 900, min_margin_pct: 20,
    competitor_prices: [{ name: "Alza", price: 1149 }, { name: "Mall", price: 1179 }, { name: "Datart", price: 1189 }] },
  { product_name: "Dyson V15 Detect", your_price: 749, your_cost: 530, min_margin_pct: 25,
    competitor_prices: [{ name: "Alza", price: 779 }, { name: "Mall", price: 769 }] },
  { product_name: "Sandalwood hodinky", your_price: 89, your_cost: 28, min_margin_pct: 50,
    competitor_prices: [{ name: "TimeWood", price: 95 }, { name: "WoodCo", price: 109 }, { name: "Aliexpress", price: 35 }] },
];

export default function AgentOsDemoPricingIntel() {
  const [form, setForm] = useState({
    product_name: EXAMPLES[0].product_name,
    your_price: EXAMPLES[0].your_price,
    your_cost: EXAMPLES[0].your_cost,
    min_margin_pct: EXAMPLES[0].min_margin_pct,
    competitor_prices: EXAMPLES[0].competitor_prices,
    lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };
  const addComp = () => setForm((f) => ({ ...f, competitor_prices: [...f.competitor_prices, { name: "", price: 0 }] }));
  const removeComp = (i) => setForm((f) => ({ ...f, competitor_prices: f.competitor_prices.filter((_, idx) => idx !== i) }));
  const setComp = (i, k, v) => setForm((f) => ({ ...f, competitor_prices: f.competitor_prices.map((c, idx) => idx === i ? { ...c, [k]: k === "price" ? parseFloat(v) || 0 : v } : c) }));

  const submit = async (e) => {
    e.preventDefault();
    const comps = form.competitor_prices.filter((c) => c.name.trim() && c.price > 0);
    if (!form.product_name.trim() || form.your_price <= 0) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/pricing-intel`, { ...form, competitor_prices: comps });
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const ActionIcon = ({ action }) => {
    if (action === "raise") return <TrendingUp className="w-7 h-7 text-emerald-400" />;
    if (action === "cut") return <TrendingDown className="w-7 h-7 text-amber-400" />;
    return <Minus className="w-7 h-7 text-gray-400" />;
  };
  const actionLabel = { raise: "Zvýš cenu", cut: "Zníž cenu", hold: "Udrž cenu" };

  const Left = (
    <form onSubmit={submit} className="p-6 bg-black border border-gray-800" data-testid="pricing-intel-form">
      <div className="flex gap-2 mb-5 flex-wrap">
        {EXAMPLES.map((_, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)} data-testid={`pricing-intel-example-${i}`}
            className="text-[10px] px-3 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/20 font-bold uppercase tracking-widest">
            Príklad {i + 1}
          </button>
        ))}
      </div>
      <Field label="Produkt"><input required value={form.product_name} maxLength={200}
        onChange={(e) => setForm({ ...form, product_name: e.target.value })}
        className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm placeholder-gray-600 focus:border-emerald-500 outline-none"
        data-testid="pricing-intel-input-product" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Tvoja cena (€)"><input required type="number" step="0.01" min="0" value={form.your_price || ""}
          onChange={(e) => setForm({ ...form, your_price: parseFloat(e.target.value) || 0 })}
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none"
          data-testid="pricing-intel-input-price" /></Field>
        <Field label="Tvoja nákup. cena (€)"><input type="number" step="0.01" min="0" value={form.your_cost || ""}
          onChange={(e) => setForm({ ...form, your_cost: parseFloat(e.target.value) || 0 })}
          className="w-full px-4 py-2.5 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none"
          data-testid="pricing-intel-input-cost" /></Field>
      </div>
      <Field label={`Min. margin (${form.min_margin_pct}%)`}>
        <input type="range" min="0" max="100" step="1" value={form.min_margin_pct}
          onChange={(e) => setForm({ ...form, min_margin_pct: parseInt(e.target.value) })}
          className="w-full" data-testid="pricing-intel-input-margin" />
      </Field>
      <div className="mt-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Konkurenti</span>
          <button type="button" onClick={addComp} className="text-[10px] text-emerald-300 hover:text-emerald-200 font-bold uppercase tracking-widest inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Pridať
          </button>
        </div>
        {form.competitor_prices.map((c, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={c.name} maxLength={120} onChange={(e) => setComp(i, "name", e.target.value)}
              placeholder="Konkurent" data-testid={`pricing-intel-input-competitor-${i}-name`}
              className="flex-1 px-3 py-2 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" />
            <input type="number" step="0.01" min="0" value={c.price || ""} onChange={(e) => setComp(i, "price", e.target.value)}
              placeholder="€" data-testid={`pricing-intel-input-competitor-${i}-price`}
              className="w-24 px-3 py-2 bg-neutral-950 border border-gray-800 text-white text-sm focus:border-emerald-500 outline-none" />
            {form.competitor_prices.length > 1 && (
              <button type="button" onClick={() => removeComp(i)} className="px-2 text-gray-500 hover:text-red-400"><X className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
      <button type="submit" disabled={status.loading || !form.product_name || form.your_price <= 0}
        data-testid="pricing-intel-submit"
        className="w-full mt-4 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:bg-gray-700 disabled:text-gray-400 text-black font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2">
        {status.loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzujem…</> : <><Sparkles className="w-4 h-4" /> Analyzuj cenu</>}
      </button>
    </form>
  );

  const Right = (
    <div className="p-6 bg-black border border-gray-800" data-testid="pricing-intel-output">
      {!result && !status.loading && !status.error && <Placeholder Icon={TrendingUp} text="AI ti porovná cenu s konkurenciou a navrhne optimálny krok" />}
      {status.loading && <Loading text="AI vyhodnocuje cenovú pozíciu…" />}
      {status.error && <ErrorBox err={status.error} />}
      {result?.output && (
        <div className="space-y-4 text-sm">
          <div className="flex items-center gap-4 p-4 bg-neutral-950 border border-gray-900">
            <ActionIcon action={result.output.action} />
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Odporúčaná akcia</p>
              <p className="text-2xl font-black text-white" data-testid="pricing-intel-output-action">{actionLabel[result.output.action] || result.output.action}</p>
              <p className="text-emerald-300 text-sm font-bold tabular-nums">{result.output.current_price.toFixed(2)}€ → {result.output.suggested_price.toFixed(2)}€</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Stat label="Konkurent priemer" value={`${result.output.competitor_avg}€`} />
            <Stat label="Konkurent min" value={`${result.output.competitor_min}€`} />
            <Stat label="Margin floor" value={`${result.output.margin_floor}€`} />
          </div>
          <div className="pt-3 border-t border-gray-900">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Odôvodnenie</p>
            <p className="text-gray-200 leading-relaxed">{result.output.rationale}</p>
          </div>
          {result.output.risks?.length > 0 && (
            <div className="pt-3 border-t border-gray-900">
              <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Riziká</p>
              <ul className="space-y-1">{result.output.risks.map((r, i) => <li key={i} className="text-xs text-gray-400">• {r}</li>)}</ul>
            </div>
          )}
          {result.output.expected_outcome && (
            <div className="pt-3 border-t border-gray-900"><p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Očakávaný výsledok</p>
              <p className="text-gray-200 italic text-sm">{result.output.expected_outcome}</p></div>
          )}
          <p className="text-[10px] text-gray-600 pt-3 border-t border-gray-900">Zostáva {result.remaining_today} demo runov. Latencia: {result.latency_ms}ms.</p>
        </div>
      )}
    </div>
  );

  return <AgentOsDemoShell testid="agent-os-demo-pricing-intel" h1="Pricing Intelligence" lead="AI porovná tvoju cenu s konkurenciou, navrhne optimálny krok a chráni tvoju margin." left={Left} right={Right} />;
}

const Stat = ({ label, value }) => (<div className="p-2 bg-neutral-950 border border-gray-900"><p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{label}</p><p className="text-sm font-black text-emerald-300 tabular-nums">{value}</p></div>);
const Field = ({ label, children }) => (<label className="block mb-3"><span className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">{label}</span>{children}</label>);
const Placeholder = ({ Icon, text }) => (<div className="text-center py-10"><Icon className="w-10 h-10 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-500">{text}</p></div>);
const Loading = ({ text }) => (<div className="text-center py-10"><Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-3" /><p className="text-sm text-gray-400">{text}</p></div>);
const ErrorBox = ({ err }) => (<div className="p-4 border border-red-900/60 bg-red-950/30 text-sm text-red-300" data-testid="pricing-intel-error">{err.message || "Skús to znova."}</div>);
